/*
      This service is intended to get some metrics on the number of open tabs
      a user may have open. I anticipate this will be removed in the future
      as we move to a alternate solutions for mutli-tab support.
*/

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ValueType } from '@opentelemetry/api';
import { take } from 'rxjs';
import { tap } from 'rxjs/operators';

import { UserModelState } from '@ninety/platform/state/app-entities/users/users-state.model';
import { selectCurrentUser } from '@ninety/platform/state/app-entities/users/users-state.selectors';

import { OtelMeterService } from '../otel-meter.service';

@Injectable({ providedIn: 'root' })
export class OpenTabsMetricService {
  private static readonly TAB_KEY = 'ninety_open_tabs';
  private static readonly TAB_ID = `${Date.now()}_${Math.random()}`;
  private readonly gaugeName = 'ng-ninety.open_tabs_per_user';
  private readonly gaugeDescription = 'Number of open tabs for app.ninety.io per user/browser';
  private readonly gaugeUnit = '1';
  private currentUser: UserModelState;

  constructor(
    private otelMeterService: OtelMeterService,
    private store: Store
  ) {
    this.store
      .select(selectCurrentUser)
      .pipe(
        take(1),
        tap((user: UserModelState) => {
          this.currentUser = user;
        })
      )
      .subscribe();

    // Initialize the tab count in localStorage
    this.updateTabCount(1);

    window.addEventListener('beforeunload', this.handleUnload);

    if (this.currentUser) {
      otelMeterService
        .getObservableGauge(this.gaugeName, {
          description: this.gaugeDescription,
          unit: this.gaugeUnit,
          valueType: ValueType.INT,
        })
        .addCallback(observableResult => {
          observableResult.observe(this.getCurrentTabCount(), {
            userId: this.currentUser._id,
            personId: this.currentUser.personId,
            companyId: this.currentUser.company?._id,
          });
        });
    }
  }

  private handleUnload = () => {
    this.updateTabCount(-1);
    window.removeEventListener('beforeunload', this.handleUnload);
  };

  private updateTabCount(delta: number) {
    const tabs = this.getTabIds();
    if (delta > 0) {
      tabs.push(OpenTabsMetricService.TAB_ID);
    } else {
      const idx = tabs.indexOf(OpenTabsMetricService.TAB_ID);
      if (idx !== -1) tabs.splice(idx, 1);
    }
    localStorage.setItem(OpenTabsMetricService.TAB_KEY, JSON.stringify(tabs));
  }

  getCurrentTabCount(): number {
    return this.getTabIds().length;
  }

  private getTabIds(): string[] {
    try {
      const raw = localStorage.getItem(OpenTabsMetricService.TAB_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }
}
